import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { adminUrl } from './api';
import {
  AccessLogEntry, AccessLogFilters, AccessLogSummaryRow, EMPTY_ACCESS_FILTERS,
} from './types';

const PAGE_SIZE = 200;

function accessFilterParams(f: AccessLogFilters): Record<string, string | null> {
  return { from: f.from, to: f.to, email: f.email, method: f.method };
}

function statusColor(status: number | null): 'success' | 'warning' | 'error' | 'default' {
  if (status == null) return 'default';
  if (status >= 500) return 'error';
  if (status >= 400) return 'warning';
  if (status >= 200 && status < 300) return 'success';
  return 'default';
}

export const AccessLog: React.FC = () => {
  const [view, setView] = useState<'summary' | 'detail'>('summary');
  const [filters, setFilters] = useState<AccessLogFilters>(EMPTY_ACCESS_FILTERS);
  const [summary, setSummary] = useState<AccessLogSummaryRow[]>([]);
  const [entries, setEntries] = useState<AccessLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (nextOffset = 0) => {
    setLoading(true);
    setError(null);
    try {
      const base = accessFilterParams(filters);
      if (view === 'summary') {
        const res = await fetch(adminUrl('access-log/summary', base));
        if (res.status === 403) { setError('Not authorized — admin only.'); return; }
        if (!res.ok) throw new Error(`Server error (${res.status})`);
        const data = await res.json();
        setSummary(data.summary || []);
      } else {
        const res = await fetch(adminUrl('access-log', {
          ...base, limit: String(PAGE_SIZE), offset: String(nextOffset),
        }));
        if (res.status === 403) { setError('Not authorized — admin only.'); return; }
        if (!res.ok) throw new Error(`Server error (${res.status})`);
        const data = await res.json();
        setEntries(data.entries || []);
        setTotal(data.total || 0);
        setOffset(nextOffset);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load access log');
    } finally {
      setLoading(false);
    }
  }, [filters, view]);

  useEffect(() => { load(0); }, [load]);

  const setField = (k: keyof AccessLogFilters, v: string) =>
    setFilters((prev) => ({ ...prev, [k]: v || null }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Access Log</Typography>
        <ToggleButtonGroup
          size="small" exclusive value={view}
          onChange={(_, v) => v && setView(v)}
        >
          <ToggleButton value="summary">By user / day</ToggleButton>
          <ToggleButton value="detail">All requests</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="From" type="date" size="small" InputLabelProps={{ shrink: true }}
            value={filters.from || ''} onChange={(e) => setField('from', e.target.value)}
          />
          <TextField
            label="To" type="date" size="small" InputLabelProps={{ shrink: true }}
            value={filters.to || ''} onChange={(e) => setField('to', e.target.value)}
          />
          <TextField
            label="Email contains" size="small"
            value={filters.email || ''} onChange={(e) => setField('email', e.target.value)}
          />
          <TextField
            label="Method" size="small" select sx={{ minWidth: 120 }}
            value={filters.method || ''} onChange={(e) => setField('method', e.target.value)}
          >
            <MenuItem value="">Any</MenuItem>
            {['GET', 'POST', 'PATCH', 'PUT', 'DELETE'].map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </TextField>
          <Button
            startIcon={<RefreshIcon />} variant="outlined" size="small"
            onClick={() => load(0)}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {!loading && view === 'summary' && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Day (PT)</TableCell>
                <TableCell>User</TableCell>
                <TableCell align="right">Requests</TableCell>
                <TableCell align="right">Endpoints</TableCell>
                <TableCell align="right">IPs</TableCell>
                <TableCell>First</TableCell>
                <TableCell>Last</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No access recorded for this range.
                </TableCell></TableRow>
              )}
              {summary.map((r, i) => (
                <TableRow key={`${r.email}-${r.day}-${i}`} hover>
                  <TableCell>{r.day}</TableCell>
                  <TableCell>{r.email || <em>(unknown)</em>}</TableCell>
                  <TableCell align="right">{r.requests}</TableCell>
                  <TableCell align="right">{r.distinctPaths}</TableCell>
                  <TableCell align="right">{r.distinctIps}</TableCell>
                  <TableCell>{r.firstPt}</TableCell>
                  <TableCell>{r.lastPt}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && view === 'detail' && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time (PT)</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Path</TableCell>
                  <TableCell align="right">Status</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell align="right">ms</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No access recorded for this range.
                  </TableCell></TableRow>
                )}
                {entries.map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.atPt}</TableCell>
                    <TableCell>{e.email || <em>(unknown)</em>}</TableCell>
                    <TableCell>{e.method}</TableCell>
                    <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={e.userAgent || ''}><span>{e.path}</span></Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {e.status != null && (
                        <Chip label={e.status} size="small" color={statusColor(e.status)} variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.ip || '—'}</TableCell>
                    <TableCell align="right">{e.durationMs ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {total === 0 ? '0' : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" disabled={offset === 0} onClick={() => load(Math.max(offset - PAGE_SIZE, 0))}>
                Previous
              </Button>
              <Button size="small" disabled={offset + PAGE_SIZE >= total} onClick={() => load(offset + PAGE_SIZE)}>
                Next
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
